import React, { useState, useEffect } from 'react';
import { Layout, Save, AlertCircle, Info, Smartphone, Eye, Video, Gift, RefreshCw, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

interface AdUnitIds {
  banner: string;
  native: string;
  interstitial: string;
  rewarded: string;
  appOpen: string;
}

interface AdSettings {
  enabled: boolean;
  showToFreeOnly: boolean;
  showToPaidUsers: boolean;
  adUnitIds: {
    android: AdUnitIds;
    ios: AdUnitIds;
  };
  nativeAdFrequency: number;
}

export default function AdminAdSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<AdSettings>({
    enabled: false,
    showToFreeOnly: true,
    showToPaidUsers: false,
    adUnitIds: {
      android: { banner: '', native: '', interstitial: '', rewarded: '', appOpen: '' },
      ios: { banner: '', native: '', interstitial: '', rewarded: '', appOpen: '' }
    },
    nativeAdFrequency: 5
  });
  
  const [originalSettings, setOriginalSettings] = useState<AdSettings | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (originalSettings) {
      setHasChanges(JSON.stringify(settings) !== JSON.stringify(originalSettings));
    }
  }, [settings, originalSettings]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.get(`${API_URL}/admin/ad-settings`);

      if (response.data.success) {
        setSettings(response.data.data);
        setOriginalSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching ad settings:', error);
      toast.error('Failed to load ad settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const response = await axios.put(`${API_URL}/admin/ad-settings`, settings);

      if (response.data.success) {
        toast.success('Ad settings saved successfully');
        setOriginalSettings(settings);
        setHasChanges(false);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update ad settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (originalSettings) {
      setSettings(originalSettings);
      setHasChanges(false);
    }
  };

  const updateAdUnit = (platform: 'android' | 'ios', type: keyof AdUnitIds, value: string) => {
    setSettings(prev => ({
      ...prev,
      adUnitIds: {
        ...prev.adUnitIds,
        [platform]: {
          ...prev.adUnitIds[platform],
          [type]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Google Ads (AdMob) Settings</h2>
        <p className="text-gray-600 mt-1">Configure advertisement units and targeting for the mobile app</p>
        <div className="mt-3 flex gap-2">
          {settings.enabled ? (
            <Badge className="bg-green-600">Ads: Enabled</Badge>
          ) : (
            <Badge variant="secondary">Ads: Disabled</Badge>
          )}
          {settings.showToFreeOnly && (
            <Badge variant="outline" className="border-blue-600 text-blue-600">Free Users Only</Badge>
          )}
          {settings.showToPaidUsers && (
            <Badge variant="outline" className="border-rose-600 text-rose-600">Paid Users Incl.</Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-rose-600">
                <Smartphone className="h-5 w-5" />
                Ad Unit Configuration
              </CardTitle>
              <CardDescription>Enter your AdMob Unit IDs for each platform</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="android">
                <TabsList className="mb-4">
                  <TabsTrigger value="android">Android</TabsTrigger>
                  <TabsTrigger value="ios">iOS</TabsTrigger>
                </TabsList>
                
                {['android', 'ios'].map((platform) => (
                  <TabsContent key={platform} value={platform} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Layout className="h-4 w-4 text-gray-500" />
                          Banner Ad ID
                        </Label>
                        <Input 
                          placeholder="ca-app-pub-xxx/yyy" 
                          value={settings.adUnitIds[platform as 'android'|'ios'].banner}
                          onChange={(e) => updateAdUnit(platform as 'android'|'ios', 'banner', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Eye className="h-4 w-4 text-gray-500" />
                          Native Ad ID
                        </Label>
                        <Input 
                          placeholder="ca-app-pub-xxx/yyy" 
                          value={settings.adUnitIds[platform as 'android'|'ios'].native}
                          onChange={(e) => updateAdUnit(platform as 'android'|'ios', 'native', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-gray-500" />
                          Interstitial Ad ID
                        </Label>
                        <Input 
                          placeholder="ca-app-pub-xxx/yyy" 
                          value={settings.adUnitIds[platform as 'android'|'ios'].interstitial}
                          onChange={(e) => updateAdUnit(platform as 'android'|'ios', 'interstitial', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Gift className="h-4 w-4 text-gray-500" />
                          Rewarded Ad ID
                        </Label>
                        <Input 
                          placeholder="ca-app-pub-xxx/yyy" 
                          value={settings.adUnitIds[platform as 'android'|'ios'].rewarded}
                          onChange={(e) => updateAdUnit(platform as 'android'|'ios', 'rewarded', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Layout className="h-4 w-4 text-gray-500" />
                          App Open Ad ID
                        </Label>
                        <Input 
                          placeholder="ca-app-pub-xxx/yyy" 
                          value={settings.adUnitIds[platform as 'android'|'ios'].appOpen}
                          onChange={(e) => updateAdUnit(platform as 'android'|'ios', 'appOpen', e.target.value)}
                        />
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-rose-600">
                <BarChart3 className="h-5 w-5" />
                Ad Display Controls
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-start justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base">Master Ad Toggle</Label>
                  <p className="text-sm text-gray-500">Completely enable or disable all ads in the app</p>
                </div>
                <Switch 
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, enabled: checked }))}
                />
              </div>

              <div className="flex items-start justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base">Target Free Users Only</Label>
                  <p className="text-sm text-gray-500">Hide all ads automatically for users with active premium plans</p>
                </div>
                <Switch 
                  checked={settings.showToFreeOnly}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, showToFreeOnly: checked }))}
                />
              </div>

              <div className="flex items-start justify-between p-4 border rounded-lg">
                <div className="space-y-1">
                  <Label className="text-base">Show Ads to Paid Users</Label>
                  <p className="text-sm text-gray-500">Enable this to show ads even to users with active premium subscriptions</p>
                </div>
                <Switch 
                  checked={settings.showToPaidUsers}
                  onCheckedChange={(checked) => setSettings(s => ({ ...s, showToPaidUsers: checked }))}
                />
              </div>

              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-medium">Native Ad Frequency</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-rose-600">Every {settings.nativeAdFrequency} items</span>
                  </div>
                </div>
                <Input 
                  type="range"
                  min="3"
                  max="20"
                  step="1"
                  value={settings.nativeAdFrequency}
                  onChange={(e) => setSettings(s => ({ ...s, nativeAdFrequency: parseInt(e.target.value) }))}
                  className="w-full h-2 bg-rose-200 rounded-lg appearance-none cursor-pointer accent-rose-600"
                />
                <p className="text-xs text-gray-500 italic">Example: A native ad will appear after every {settings.nativeAdFrequency} profile cards in lists.</p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="bg-amber-50 border-amber-200">
            <CardHeader>
              <CardTitle className="text-amber-900 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Wait!
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-amber-800 space-y-3">
              <p>Adding Google Ads requires a <strong>new build</strong> of your app because it includes native code.</p>
              <p>Once you save these settings, they will take effect on any app versions that have the AdMob SDK integrated.</p>
              <div className="p-3 bg-white rounded border border-amber-200 flex items-start gap-2">
                <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                <p className="text-xs">Ensure you have added your App IDs to <code>app.json</code> before generating the final production build.</p>
              </div>
            </CardContent>
          </Card>

          {hasChanges && (
            <Card className="border-rose-200 bg-rose-50 ring-1 ring-rose-200">
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center gap-2 text-rose-800">
                  <AlertCircle className="h-5 w-5" />
                  <span className="font-semibold">Unsaved Changes</span>
                </div>
                <p className="text-sm text-rose-700">You have modified ad settings. Save them to apply to the mobile app.</p>
                <div className="flex gap-2">
                  <Button 
                    className="flex-1 bg-rose-600 hover:bg-rose-700"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : <><Save className="h-4 w-4 mr-2" /> Save</>}
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={handleReset}>
                    <RefreshCw className="h-4 w-4 mr-2" /> Reset
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
